import { FinancialInvestment } from './financial-investment/financial-investment.model';
import { InputSummary } from '../../input-summary';
import { Feedstock } from '../feedstock/feedstock.model';
import { FinancialIncome } from './financial-income.model';
import { FinancialCapex } from './financial-capex.model';
import { FinancialOpex } from './financial-opex.model';
import { UnitValue } from '../../data-structures/unit-value.model';
import { PlantSpecifications, PlantSpecificationsPropName } from '../plant-specifications/plant-specifications.model';

export interface ProjectFinancialInterface {
  income: FinancialIncome[];
  capex: FinancialCapex[];
  opex: FinancialOpex[];
  investment: FinancialInvestment;
  savedTotalIncomeUnit: string;
  savedTotalCapexUnit: string;
  savedTotalOpexUnit: string;
  savedDefaultLifetime: UnitValue;
  lastOpexEstPercentage: UnitValue;
}

export class ProjectFinancial {
  income: FinancialIncome[] = [];
  capex: FinancialCapex[] = [];
  opex: FinancialOpex[] = [];
  investment: FinancialInvestment;
  savedTotalIncomeUnit: string;
  savedTotalCapexUnit: string;
  savedTotalOpexUnit: string;
  savedDefaultLifetime: UnitValue;
  lastOpexEstPercentage: UnitValue;

  constructor(param: ProjectFinancialInterface = {
    income: [],
    capex: [],
    opex: [],
    investment: new FinancialInvestment(),
    savedTotalIncomeUnit: null,
    savedTotalCapexUnit: null,
    savedTotalOpexUnit: null,
    savedDefaultLifetime: new UnitValue(),
    lastOpexEstPercentage: new UnitValue(),
  }) {
    this.income = [];
    if (param.income) {
      for (const income of param.income) {
        this.income.push(new FinancialIncome(income));
      }
    }
    this.capex = [];
    if (param.capex) {
      for (const item of param.capex) {
        this.capex.push(new FinancialCapex(item));
      }
    }
    this.opex = [];
    if (param.opex) {
      for (const item of param.opex) {
        this.opex.push(new FinancialOpex(item));
      }
    }
    this.investment = new FinancialInvestment(param.investment);
    this.savedTotalIncomeUnit = param.savedTotalIncomeUnit;
    this.savedTotalCapexUnit = param.savedTotalCapexUnit;
    this.savedTotalOpexUnit = param.savedTotalOpexUnit;
    this.savedDefaultLifetime = new UnitValue(param.savedDefaultLifetime);
    this.lastOpexEstPercentage = new UnitValue(param.lastOpexEstPercentage);
  }

  static deepCopy(financial: ProjectFinancial) {
    let copy;
    if (financial) {
      copy = new ProjectFinancial(financial);
      return copy;
    }
    throw new Error('Financial copy failed');
  }

  getInputSummary(mats: Feedstock[], plantSpec: PlantSpecifications) {
    const inputSummary = new InputSummary();
    this.getMatFinancialInputSummary(inputSummary, mats);
    this.getGenProductInputSummary(inputSummary, plantSpec);
    this.getAdditionalIncomeInputSummary(inputSummary);
    this.getConFuelInputSummary(inputSummary, plantSpec);
    this.getCapexInputSummary(inputSummary);
    this.getOpexInputSummary(inputSummary);
    inputSummary.subSection[FinancialPropName.investment] = this.investment.getInputSummary(['financial', 'investment']);
    return inputSummary;
  }

  getMatFinancialInputSummary(inputSummary: InputSummary, mats: Feedstock[]) {
    if (mats && mats.length > 0) {
      const feedFinSum = new InputSummary();
      for (const mat of mats) {
        const index = mats.indexOf(mat);
        feedFinSum.subSection[mat.name] = mat.getFinancialInputSummary(['feedstocks', String(index)]);
      }
      inputSummary.subSection[FinancialPropName.feedstock] = feedFinSum;
    }
    return inputSummary;
  }

  getGenProductInputSummary(inputSummary: InputSummary, plantSpec: PlantSpecifications) {
    if (plantSpec) {
      if (plantSpec.generatedProducts && plantSpec.generatedProducts.length > 0) {
        const powFinSum = new InputSummary();
        for (const pow of plantSpec.generatedProducts) {
          const index = plantSpec.generatedProducts.indexOf(pow);
          powFinSum.subSection[pow.name] = pow.getFinancialInputSummary(['plantSpecifications', 'generatedProducts', String(index)]);
        }
        inputSummary.subSection[PlantSpecificationsPropName.generatedProducts] =
          powFinSum;
      }
    }
    return inputSummary;
  }

  getAdditionalIncomeInputSummary(inputSummary: InputSummary) {
    if (this.income && this.income.length > 0) {
      const incomeSummary = new InputSummary();
      for (const income of this.income) {
        const index = this.income.indexOf(income);
        incomeSummary.subSection[income.name] = income.getInputSummary(['financial', 'income', String(index)]);
      }
      inputSummary.subSection[FinancialPropName.income] = incomeSummary;
    }
    return inputSummary;
  }

  getCapexInputSummary(inputSummary: InputSummary) {
    if (this.capex && this.capex.length > 0) {
      const capexSummary = new InputSummary();
      for (const capex of this.capex) {
        const index = this.capex.indexOf(capex);
        capexSummary.subSection[capex.name] = capex.getInputSummary(['financial', 'capex', String(index)]);
      }
      inputSummary.subSection[FinancialPropName.capex] = capexSummary;
    }
    return inputSummary;
  }

  getOpexInputSummary(inputSummary: InputSummary) {
    if (this.opex && this.opex.length > 0) {
      const opexSummary = new InputSummary();
      for (const opex of this.opex) {
        const index = this.opex.indexOf(opex);
        opexSummary.subSection[opex.name] = opex.getInputSummary(['financial', 'opex', String(index)], opex);
      }
      inputSummary.subSection[FinancialPropName.opex] = opexSummary;
    }
    return inputSummary;
  }

  getConFuelInputSummary(inputSummary: InputSummary, plantSpec: PlantSpecifications) {
    if (plantSpec ) {
      if (plantSpec.consumedFuels && plantSpec.consumedFuels.length > 0) {
        const powFinSum = new InputSummary();
        for (const pow of plantSpec.consumedFuels) {
          const index = plantSpec.consumedFuels.indexOf(pow);
          powFinSum.subSection[pow.name] = pow.getFinancialInputSummary(['plantSpecifications', 'consumedFuels', String(index)]);
          inputSummary.subSection[FinancialPropName.consumedFuels] =
            powFinSum;
        }
      }
    }
    return inputSummary;
  }
}

export enum FinancialPropName {
  feedstock = 'INPUT_SUMMARY.FINANCIAL.FEEDSTOCK',
  consumedFuels = 'INPUT_SUMMARY.FINANCIAL.CONSUMED_FUELS',
  income = 'INPUT_SUMMARY.FINANCIAL.INCOME.TITLE',
  capex = 'INPUT_SUMMARY.FINANCIAL.CAPEX.TITLE',
  opex = 'INPUT_SUMMARY.FINANCIAL.OPEX.TITLE',
  investment = 'INPUT_SUMMARY.FINANCIAL.INVESTMENT.TITLE',
  id_feedstock = 'feedstock',
  id_consumedFuels = 'utilities and consumed fuels',
  id_income = 'additional income',
  id_capex = 'capital expenditure',
  id_opex = 'operational expenditure',
  id_investment = 'investment',
  additional_products = "additional products"
}
